import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher, customFetcherRaw } from '../hooks/customFetcher';
import {
    BlogComment,
    BlogCommentCreateInput,
    BlogImage,
    BlogPostCreateInput,
    BlogPostDetail,
    BlogPostsPage,
    BlogPostUpdateInput,
    BlogReactionCounts,
    BlogReactionKind,
    blogCommentCreateInputSchema,
    blogCommentListSchema,
    blogCommentSchema,
    blogImageSchema,
    blogPostCreateInputSchema,
    blogPostDetailSchema,
    blogPostsPageSchema,
    blogPostsQueryInputSchema,
    blogPostUpdateInputSchema,
} from './blogSchemas';

export const blogQueryKeys = {
    posts: (page: number, pageSize: number) => ['blogPosts', page, pageSize] as const,
    post: (id: number) => ['blogPost', id] as const,
    comments: (postId: number) => ['blogComments', postId] as const,
    images: (postId: number) => ['blogImages', postId] as const,
};

export const useBlogPosts = (page: number, pageSize: number) => {
    const parsed = blogPostsQueryInputSchema.parse({ page, pageSize });

    return useQuery<BlogPostsPage, Error>({
        queryKey: blogQueryKeys.posts(parsed.page, parsed.pageSize),
        queryFn: async () => {
            const response = await customFetcher<unknown>({
                endpoint: `/blog/posts?page=${parsed.page}&page_size=${parsed.pageSize}`,
            });
            return blogPostsPageSchema.parse(response);
        },
        refetchOnWindowFocus: false,
    });
};

export const useBlogPost = (id: number) => useQuery<BlogPostDetail, Error>({
    queryKey: blogQueryKeys.post(id),
    queryFn: async () => {
        const response = await customFetcher<unknown>({ endpoint: `/blog/posts/${id}` });
        return blogPostDetailSchema.parse(response);
    },
    enabled: id > 0,
    refetchOnWindowFocus: false,
});

export const useBlogImages = (postId: number) => useQuery<BlogImage[], Error>({
    queryKey: blogQueryKeys.images(postId),
    queryFn: async () => {
        const response = await customFetcher<unknown>({ endpoint: `/blog/posts/${postId}` });
        const detail = blogPostDetailSchema.parse(response);
        return detail.images;
    },
    enabled: postId > 0,
    refetchOnWindowFocus: false,
});

export const useCreateBlogPost = () => {
    const queryClient = useQueryClient();

    return useMutation<BlogPostDetail, Error, BlogPostCreateInput>({
        mutationFn: async (input) => {
            const parsed = blogPostCreateInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: '/blog/posts',
                method: 'POST',
                body: parsed,
            });
            return blogPostDetailSchema.parse(response);
        },
        onSuccess: async (createdPost) => {
            await queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.post(createdPost.id) });
        },
    });
};

export const useUpdateBlogPost = () => {
    const queryClient = useQueryClient();

    return useMutation<BlogPostDetail, Error, BlogPostUpdateInput>({
        mutationFn: async (input) => {
            const parsed = blogPostUpdateInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: `/blog/posts/${parsed.id}`,
                method: 'PATCH',
                body: {
                    title: parsed.title,
                    teaser: parsed.teaser,
                    content: parsed.content,
                    tag_ids: parsed.tag_ids,
                },
            });
            return blogPostDetailSchema.parse(response);
        },
        onSuccess: async (updatedPost) => {
            await queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.post(updatedPost.id) });
        },
    });
};

export const useDeleteBlogPost = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { id: number }>({
        mutationFn: async ({ id }) => {
            await customFetcher<void>({
                endpoint: `/blog/posts/${id}`,
                method: 'DELETE',
            });
        },
        onSuccess: async (_data, { id }) => {
            await queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
            queryClient.removeQueries({ queryKey: blogQueryKeys.post(id) });
            queryClient.removeQueries({ queryKey: blogQueryKeys.comments(id) });
            queryClient.removeQueries({ queryKey: blogQueryKeys.images(id) });
        },
    });
};

export const useBlogComments = (postId: number) => useQuery<BlogComment[], Error>({
    queryKey: blogQueryKeys.comments(postId),
    queryFn: async () => {
        const response = await customFetcher<unknown>({
            endpoint: `/blog/posts/${postId}/comments`,
        });
        return blogCommentListSchema.parse(response);
    },
    enabled: postId > 0,
    refetchOnWindowFocus: false,
});

export const useCreateBlogComment = () => {
    const queryClient = useQueryClient();

    return useMutation<BlogComment, Error, { postId: number; input: BlogCommentCreateInput }>({
        mutationFn: async ({ postId, input }) => {
            const parsed = blogCommentCreateInputSchema.parse(input);
            const response = await customFetcher<unknown>({
                endpoint: `/blog/posts/${postId}/comments`,
                method: 'POST',
                body: parsed,
            });
            return blogCommentSchema.parse(response);
        },
        onSuccess: async (_data, { postId }) => {
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.comments(postId) });
        },
    });
};

export const useToggleBlogCommentReaction = () => {
    const queryClient = useQueryClient();

    return useMutation<
        { comment_id: number; viewer_emoji: BlogReactionKind | null; counts: BlogReactionCounts },
        Error,
        { postId: number; commentId: number; emoji: BlogReactionKind }
    >({
        mutationFn: async ({ commentId, emoji }) => {
            const response = await customFetcher<unknown>({
                endpoint: `/blog/comments/${commentId}/reactions`,
                method: 'POST',
                body: { emoji },
            });
            return response as { comment_id: number; viewer_emoji: BlogReactionKind | null; counts: BlogReactionCounts };
        },
        onSuccess: async (_data, { postId }) => {
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.comments(postId) });
        },
    });
};

export const useDeleteBlogComment = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { postId: number; commentId: number }>({
        mutationFn: async ({ commentId }) => {
            await customFetcher<void>({
                endpoint: `/blog/comments/${commentId}`,
                method: 'DELETE',
            });
        },
        onSuccess: async (_data, { postId }) => {
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.comments(postId) });
        },
    });
};

export const useUploadBlogImages = () => {
    const queryClient = useQueryClient();

    return useMutation<BlogImage[], Error, { postId: number; files: File[] }>({
        mutationFn: async ({ postId, files }) => {
            if (files.length === 0) {
                throw new Error('Bitte mindestens ein Bild auswählen.');
            }
            const formData = new FormData();
            files.forEach((file) => formData.append('file', file));

            const response = await customFetcher<unknown>({
                endpoint: `/blog/posts/${postId}/images`,
                method: 'POST',
                body: formData,
            });

            if (Array.isArray(response)) {
                return response.flatMap((item) => {
                    const parsed = blogImageSchema.safeParse(item);
                    return parsed.success ? [parsed.data] : [];
                });
            }

            if (response && typeof response === 'object') {
                const parsedSingle = blogImageSchema.safeParse(response);
                if (parsedSingle.success) {
                    return [parsedSingle.data];
                }
            }

            return [];
        },
        onSuccess: async (_data, { postId }) => {
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.images(postId) });
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.post(postId) });
            await queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
        },
    });
};

export const useDeleteBlogImage = () => {
    const queryClient = useQueryClient();

    return useMutation<void, Error, { imageId: number; postId: number }, { previousImages?: BlogImage[] }>({
        mutationFn: async ({ imageId }) => {
            await customFetcher<void>({
                endpoint: `/blog/images/${imageId}`,
                method: 'DELETE',
            });
        },
        onMutate: async ({ imageId, postId }) => {
            await queryClient.cancelQueries({ queryKey: blogQueryKeys.images(postId) });
            const previousImages = queryClient.getQueryData<BlogImage[]>(blogQueryKeys.images(postId));
            queryClient.setQueryData<BlogImage[]>(blogQueryKeys.images(postId), (current = []) => (
                current.filter((image) => image.id !== imageId)
            ));
            return { previousImages };
        },
        onError: (_error, { postId }, context) => {
            if (context?.previousImages) {
                queryClient.setQueryData(blogQueryKeys.images(postId), context.previousImages);
            }
        },
        onSuccess: async (_data, { postId }) => {
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.images(postId) });
            await queryClient.invalidateQueries({ queryKey: blogQueryKeys.post(postId) });
            await queryClient.invalidateQueries({ queryKey: ['blogPosts'] });
        },
    });
};

export const useDownloadBlogImagesZip = () => useMutation<Blob, Error, { postId: number; imageIds: number[] }>({
    mutationFn: async ({ postId, imageIds }) => {
        if (imageIds.length === 0) {
            throw new Error('Bitte mindestens ein Bild auswählen.');
        }

        const response = await customFetcherRaw({
            endpoint: `/blog/posts/${postId}/images/download-zip`,
            method: 'POST',
            body: {
                image_ids: imageIds,
            },
        });

        return response.blob();
    },
});
